import { fetchConversationDetails, reportError } from "@noa/api-client";
import {
  discardHeldMessages,
  getLiveAssistantBubble,
  initChatbotMetadata,
  initConversation,
  restoreLiveStreamStatus,
} from "./chat-runtime";
import { useAppStore } from "./app.store";
import { useStartupStore } from "./startup.store";
import type { StartupStatus } from "./startup.types";

/** How long a session may take before the chat says the API is starting: an awake one answers well within it. */
const WARMUP_NOTICE_DELAY_MS = 3_000;
/** How long to wait for a session before offering a retry. A cold start takes about 84 s. */
const WARMUP_TIMEOUT_MS = 180_000;

/**
 * Identifies the running start, if any — nothing awaits it, so a token is enough. Collapses
 * overlapping starts into one session (an entry effect can fire twice under StrictMode), and only
 * until the new session id reaches the store (see below).
 */
let startInFlight: symbol | null = null;

/**
 * Restarts the clock of the session request still out, or null when none is. Lets a retry wait on a
 * request that is merely slow instead of opening a second session beside it.
 */
let restartWait: (() => void) | null = null;

/**
 * Keeps `useStartupStore` describing `attempt` until a session lands: nothing at first, the warmup
 * notice past `noticeDelayMs`, a failure past `WARMUP_TIMEOUT_MS` or when the attempt ends without one.
 * Readiness is read from the store rather than from `attempt`, which runs on through the greeting.
 */
const watchSessionRequest = (attempt: Promise<void>, noticeDelayMs: number): void => {
  const timers: ReturnType<typeof setTimeout>[] = [];
  const hasSession = () => Boolean(useAppStore.getState().sessionId);
  const showUnlessReady = (status: StartupStatus) => () => {
    if (!hasSession()) useStartupStore.setState({ status });
  };

  const startClock = (delayMs: number) => {
    timers.forEach(clearTimeout);
    useStartupStore.setState({ status: delayMs === 0 ? "warming" : "idle", startedAt: Date.now() });
    if (delayMs > 0) timers.push(setTimeout(showUnlessReady("warming"), delayMs));
    timers.push(setTimeout(showUnlessReady("failed"), WARMUP_TIMEOUT_MS));
  };

  const restart = () => startClock(0);
  const stop = () => {
    timers.forEach(clearTimeout);
    unsubscribe();
    if (restartWait === restart) restartWait = null;
  };

  const unsubscribe = useAppStore.subscribe((state) => {
    if (!state.sessionId) return;
    stop();
    useStartupStore.setState({ status: "idle" });
  });

  startClock(noticeDelayMs);
  restartWait = restart;
  void attempt.finally(() => {
    showUnlessReady("failed")();
    stop();
  });
};

/** Runs `attempt` as the start in flight, watched until its session lands. */
const runStart = (attempt: () => Promise<void>, noticeDelayMs: number): void => {
  const start = Symbol("conversation-start");
  startInFlight = start;

  const running = attempt();
  watchSessionRequest(running, noticeDelayMs);
  void running.finally(() => {
    // Clear only our own start — a hung one settling late must not open the guard on a newer one.
    if (startInFlight === start) startInFlight = null;
  });
};

/**
 * Assert that the blank-chat routes ("/" and predefined tasks) show a fresh session — called from
 * their entry effect (web: mount, mobile: focus). An already-pristine session is kept, so an
 * incidental re-entry doesn't regenerate the greeting; anything else resets the store and starts one.
 *
 * The only trigger for a new session: the "new conversation" buttons are pure navigation, so the
 * route is the single authority. Safe because an engaged session always lives at /conversation/:id
 * (the first-message redirect) and the buttons are disabled until then — break either and
 * navigating to a route you are already on fires no effect, so the button silently does nothing.
 */
export const assertBlankConversation = (): void => {
  const state = useAppStore.getState();
  const isPristine = Boolean(state.sessionId) && !state.getFirstUserMessage();

  if (isPristine) return;
  // Deliberately scoped to the window before the session id lands: past that the pristine check
  // above owns de-duplication, so a greeting stream that never settles (backgrounded app, dropped
  // connection) can't latch this shut and leave the blank routes unable to ever start a session.
  if (startInFlight && !state.sessionId) return;

  useAppStore.getState().resetStore();
  discardHeldMessages();
  runStart(async () => {
    await initChatbotMetadata();
    await initConversation();
  }, WARMUP_NOTICE_DELAY_MS);
};

/**
 * The warmup notice's retry. Waits again on a request still out, with a fresh timeout; otherwise asks
 * for a new session. Either way the notice shows at once, since the API is known to be slow by now.
 * Messages held so far are kept, and sent once the session lands.
 */
export const retryStartup = (): void => {
  if (restartWait) {
    restartWait();
    return;
  }
  runStart(initConversation, 0);
};

/**
 * Open an existing conversation by id — the /conversation/:id entry effect (web: mount, mobile:
 * focus). Idempotent: the live state is kept when the store already holds this session with messages
 * (e.g. the redirect right after the first message), so an in-flight stream is never wiped.
 * `setConversation` is idempotent too, so a load superseded mid-fetch can't clobber a newer one.
 *
 * A reply still streaming when the conversation was left is re-entered whole: the server can only
 * supply what it has persisted, which excludes that bubble until the stream ends, so its text and
 * sources come from `chat-runtime`'s buffer instead, and its status from the state that stream last
 * reported. The bubble is appended in the same `setConversation` call as the fetched messages, not
 * after it, so no chunk can slip in between and open a second bubble.
 */
export const loadConversation = async (sessionId: string): Promise<void> => {
  if (!sessionId) return;

  const state = useAppStore.getState();
  if (state.sessionId === sessionId && state.messages.length > 0) return;

  try {
    const conversation = await fetchConversationDetails(sessionId);
    const streamingBubble = getLiveAssistantBubble(sessionId);
    const messages = streamingBubble ? [...conversation.messages, streamingBubble] : conversation.messages;

    useAppStore.getState().setConversation({ sessionId, messages });
    // After hydration, so the status describes this conversation rather than the blank chat whose
    // greeting stream last wrote it.
    restoreLiveStreamStatus(sessionId);
  } catch (error) {
    reportError(error);
  }
};
